import {
  BlurMask,
  Canvas,
  Extrapolate,
  Group,
  interpolate,
  Path,
  Skia,
  SweepGradient,
  vec,
} from '@shopify/react-native-skia';
import React, { forwardRef, useImperativeHandle } from 'react';
import { View } from 'react-native';
import {
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { InteractiveForwardRefProps } from '@/components/interactive';

// The logarithmic spiral will depend on the index
const logarithmicSpiral = ({
  angle,
  index,
}: {
  angle: number;
  index: number;
}) => {
  'worklet';
  const a = index / 4;
  const k = 0.005;
  return {
    x: a * Math.exp(k * angle) * Math.cos(angle * index),
    y: a * Math.exp(k * angle) * Math.sin(angle * index),
  };
};

const spiralCircleCount = 1500;

type SpiralProps = {
  withFixedRadius?: boolean;
  withSweepGradient?: boolean;
  withAnimation?: boolean;
};

const Spiral = forwardRef<InteractiveForwardRefProps, SpiralProps>(
  (
    { withFixedRadius = false, withSweepGradient = true, withAnimation = true },
    ref
  ) => {
    const canvasSize = useSharedValue({ width: 0, height: 0 });

    const MAX_DISTANCE_FROM_CENTER = useDerivedValue(() => {
      return Math.sqrt(
        (canvasSize.value.width / 2) ** 2 + (canvasSize.value.height / 2) ** 2
      );
    });

    const angle = useSharedValue(Math.PI / 2);

    const spiralCoordinates = useDerivedValue(() => {
      return Array.from({ length: spiralCircleCount }, (_, index) => {
        const { x, y } = logarithmicSpiral({
          angle: angle.value,
          index,
        });
        return { x, y };
      });
    });

    const animatedSpiralCoordinatesX = useDerivedValue(() => {
      return withTiming(
        spiralCoordinates.value.map((coordinate) => {
          return coordinate.x;
        }),
        {
          duration: withAnimation ? 1500 : 0,
        }
      );
    });

    const animatedSpiralCoordinatesY = useDerivedValue(() => {
      return withTiming(
        spiralCoordinates.value.map((coordinate) => {
          return coordinate.y;
        }),
        {
          duration: withAnimation ? 1500 : 0,
        }
      );
    });

    const path = useDerivedValue(() => {
      const skPath = Skia.Path.Make();
      if (canvasSize.value.width === 0 || canvasSize.value.height === 0) {
        return skPath;
      }

      for (let index = 0; index < spiralCircleCount; index++) {
        const x = animatedSpiralCoordinatesX.value[index];
        const y = animatedSpiralCoordinatesY.value[index];

        const distanceFromCenter = Math.sqrt(x ** 2 + y ** 2);
        const radius = interpolate(
          distanceFromCenter,
          [0, MAX_DISTANCE_FROM_CENTER.value],
          [1, 0.1],
          Extrapolate.CLAMP
        );

        skPath.addCircle(x, y, withFixedRadius ? 1 : radius);
      }

      return skPath;
    });

    useImperativeHandle(ref, () => ({
      play: () => {
        angle.value = Math.PI * 2 * Math.random();
      },
      reset: () => {
        angle.value = Math.PI / 2;
      },
      pause: () => {}, // No pause functionality for this component
    }));

    const rGroupTransform = useDerivedValue(() => {
      return [
        {
          translateX: canvasSize.value.width / 2,
        },
        {
          translateY: canvasSize.value.height / 2,
        },
      ];
    });

    return (
      <View
        style={{
          flex: 1,
          backgroundColor: '#0a0a0a',
        }}
      >
        <Canvas style={{ flex: 1 }} onSize={canvasSize}>
          <Group transform={rGroupTransform}>
            <Path path={path} color={withSweepGradient ? undefined : 'white'} />

            {withSweepGradient && (
              <SweepGradient
                c={vec(0, 0)}
                colors={['cyan', 'magenta', 'yellow', 'cyan']}
              />
            )}
            {withSweepGradient && <BlurMask blur={5} style="solid" />}
          </Group>
        </Canvas>
      </View>
    );
  }
);

export { Spiral };
